import {
    UploadError,
    UploadFailedReason,
    type UploadResponse,
} from '~/state/uploader/uploadQueue'
import { PRODUCT_NAME, getCurrentLocale } from '~/config/constants'
import { canReadFileContent } from '~/state/uploader/uploadFileURL'
import { Reactions } from '~/state/reaction/actions'
import { HostUrl, downloadThroughAnchor, sendPOSTRedirect } from '../toolbox'
import type { ServiceDict } from '../externals'
import type { FetchObject } from '../FetchObject'
import type { CommentService } from '../capabilities'

export class AppService implements CommentService {
    private hostUrl: HostUrl
    private downloadHost: HostUrl

    constructor(
        private fetchObject: FetchObject,
        hosts: ServiceDict,
        authToken: string,
        foreignAuth?: string,
    ) {
        const commonQueryParams: DictionaryOf<string> = {
            // TODO: Remove optional `import.meta.env`,
            // when we can load vite env variables through `vite-register`.
            // Currently `vite-register` only loads `.env` from root, not `tests/.env`
            key: (import.meta.env?.VITE_API_KEY || 'testing') as string,
            auth: authToken,
            client_v: (import.meta.env?.VITE_VERSION || 'testing') as string,
        }
        if (foreignAuth) {
            commonQueryParams['foreign-auth'] = foreignAuth
        }

        this.hostUrl = new HostUrl(hosts.appHost, commonQueryParams)
        this.downloadHost = new HostUrl(hosts.downloadHost, commonQueryParams)
    }

    public getAccountInfo(): Promise<AccountInfo> {
        const params = {
            wantName: '0',
            app_lang: getCurrentLocale().substring(0, 2),
        }
        return this.fetchObject
            .get(this.hostUrl.getPath('/st/4/account_info', params))
            .asJson<AccountInfo>()
    }

    public setProfileName(name: string): Promise<Response> {
        return this.fetchObject
            .post(this.hostUrl.getPath(`/st/4/name`, { name }))
            .rawResponse()
    }
    public async setProfilePictureFromBlob(blob: Blob): Promise<Response> {
        const url = this.hostUrl.getPath(`/st/4/profile/picture`, {
            path: 'profile.jpg',
        })
        const payload = new FormData()
        payload.append('file', blob)

        const request = new XMLHttpRequest()
        await new Promise((success, error) => {
            request.addEventListener('load', success)
            request.addEventListener('error', error)
            request.addEventListener('abort', error)

            request.open('POST', url)
            request.send(payload)
        })
        if (request.status !== 201) {
            throw new Error(request.responseText)
        }
        return request.response
    }

    public updatePushToken(push_token: string): Promise<Response> {
        return this.fetchObject
            .post(
                this.hostUrl.getPath(`/st/4/update_push_token`, { push_token }),
            )
            .rawResponse()
    }

    public getUserOption(optionKey: string) {
        return this.fetchObject
            .get(this.hostUrl.getPath(`/st/4/options/${optionKey}`))
            .asText()
    }

    public setUserOption(optionKey: string, value: string) {
        return this.fetchObject
            .put(this.hostUrl.getPath(`/st/4/options/${optionKey}`), value)
            .rawResponse()
    }
    public deleteUserOption(optionKey: string) {
        return this.fetchObject
            .delete(this.hostUrl.getPath(`/st/4/options/${optionKey}`))
            .rawResponse()
    }

    public getConnectedDevices(): Promise<DeviceResponse> {
        return this.fetchObject
            .get(this.hostUrl.getPath('/st/4/devices'))
            .asJson<DeviceResponse>()
    }
    public deleteConnectedDevice(deviceID: string): Promise<Response> {
        return this.fetchObject
            .post(this.hostUrl.getPath(`/st/4/devices/${deviceID}/delete`))
            .rawResponse()
    }

    public logout(): Promise<Response> {
        return this.fetchObject
            .post(this.hostUrl.getPath('/st/4/logout'))
            .rawResponse()
    }
    public getJobList(): Promise<JobsResponse[]> {
        const params = {
            stories: '1',
            include_details: '1',
        }
        return this.fetchObject
            .get(this.hostUrl.getPath('/st/4/jobs', params))
            .asJson<JobsResponse[]>()
    }

    public async getDefaultJob(): Promise<DefaultJobInfo> {
        return this.fetchObject
            .get(this.hostUrl.getPath('/st/4/jobs/default'))
            .asJson<DefaultJobInfo>()
    }

    public getTimelineMonths(
        jobID: JobID,
    ): Promise<{ months: TimelineMonth[] }> {
        return this.fetchObject
            .get(this.hostUrl.getPath(`/st/4/jobs/${jobID}/timeline/months`))
            .asJson<{ months: TimelineMonth[] }>()
    }

    public getJobInfo(jobID: JobID, details = true): Promise<JobInfoResponse> {
        const params = {
            include_details: details ? '1' : '0',
        }
        return this.fetchObject
            .get(this.hostUrl.getPath(`/st/4/jobs/${jobID}/info`, params))
            .asJson<JobInfoResponse>()
    }

    public getJobChanges(jobID: JobID, since = 0): Promise<JobChange[]> {
        return this.fetchObject
            .get(
                this.hostUrl.getPath(`/st/4/jobs/${jobID}/changes`, {
                    serial: 1,
                    since,
                }),
            )
            .asJson<JobChange[]>()
    }
    public async getLastJobChangeID(jobID: JobID): Promise<number> {
        const resp = await this.fetchObject
            .get(
                this.hostUrl.getPath(`/st/4/jobs/${jobID}/changes`, {
                    since: Number.MAX_SAFE_INTEGER,
                }),
            )
            .rawResponse()
        return parseInt(resp.headers.get('x-last-event-serial') || '-1', 10)
    }

    public getJobContributors(
        jobID: JobID,
        includeProfilePicture = false,
    ): Promise<ContributingUsersResponse> {
        return this.fetchObject
            .get(
                this.hostUrl.getPath('/st/4/jobs/' + jobID + '/users', {
                    include_profile_url: includeProfilePicture ? 1 : 0,
                }),
            )
            .asJson<ContributingUsersResponse>()
    }

    public async createJob(opts: CreateJobProperties): Promise<DefaultJobInfo> {
        return this.fetchObject
            .post(this.hostUrl.getPath('/st/4/jobs', opts))
            .asJson<DefaultJobInfo>()
    }

    public publishJob(
        jobID: JobID,
        opts: PublishJobOptions,
    ): Promise<PublishJobResponse> {
        return this.fetchObject
            .post(this.hostUrl.getPath(`/st/4/jobs/${jobID}/publish`, opts))
            .asJson<PublishJobResponse>()
    }

    public deleteJob(jobID: JobID) {
        return this.fetchObject
            .delete(this.hostUrl.getPath('/st/4/jobs/' + jobID))
            .rawResponse()
    }

    public createStoryUser(name: string): Promise<CreatedStoryUser> {
        return this.fetchObject
            .post(this.hostUrl.getPath('/st/4/stories/users', { name }))
            .asJson<CreatedStoryUser>()
    }

    public async getFiles(
        jobID: string,
        options: JobFilesRequestOptions,
    ): Promise<JobFileResponse> {
        const resp = await this.fetchObject
            .get(this.hostUrl.getPath(`/st/4/jobs/${jobID}/files`, options))
            .rawResponse()
        const lastEventSerial = parseInt(
            resp.headers.get('x-last-event-serial') || '-1',
            10,
        )
        const files = (await resp.json()) as JobFile[]
        return { lastEventSerial, files }
    }

    public async getDeletedFiles(
        options: DeletedFileRequestOptions,
    ): Promise<DeletedFileResponse> {
        const resp = await this.fetchObject
            .get(this.hostUrl.getPath('/st/4/trash_can', options))
            .rawResponse()
        const deletedFiles = (await resp.json()) as DeletedFile[]
        const limit = parseInt(resp.headers.get('x-limit') || '1000', 10)
        const resultCount = parseInt(
            resp.headers.get('x-result-count') ||
                deletedFiles.length.toString(),
            10,
        )
        return { deletedFiles, limit, resultCount }
    }

    public getFileMetadata(
        jobID: JobID,
        fileID: FileID,
    ): Promise<FileMetadataResponse> {
        return this.fetchObject
            .get(this.hostUrl.getPath(`/st/4/jobs/${jobID}/metadata/${fileID}`))
            .asJson<FileMetadataResponse>()
    }

    private static getUUIDFromTextResponse(str: string): string {
        // Response-format of a successful upload/dedup: "OK [FileID] [UsedSpace] [timestamp]"
        const parts = str.split(' ')
        if (parts[0] !== 'OK') {
            throw new UploadError(
                UploadFailedReason.FileError,
                'File rejected by server',
            )
        }
        return parts[1]
    }

    public async dedupFile(
        targetJob: JobID,
        opts: Partial<JobFile & { policy: DedupPolicy }>,
    ): Promise<string> {
        const resp = await this.fetchObject
            .post(
                this.hostUrl.getPath(
                    '/st/4/jobs/' + targetJob + '/files_dedup',
                    opts,
                ),
            )
            .rawResponse()
        const contentType = resp.headers.get('content-type')

        if (contentType && resp.status === 201) {
            if (contentType.match('application/json') !== null) {
                const value: DedupResponse = await resp.json()
                return value.uuid
            } else if (contentType.match('text/html') !== null) {
                return AppService.getUUIDFromTextResponse(await resp.text())
            } else if (contentType.match('text/plain') !== null) {
                return AppService.getUUIDFromTextResponse(await resp.text())
            }
        }

        throw new UploadError(
            UploadFailedReason.FileError,
            'File rejected by server',
        )
    }

    public async copyFilesToDefaultJob(
        sourceJobID: JobID,
        fileIDs: FileID[],
    ): Promise<void> {
        await this.fetchObject
            .post(
                this.hostUrl.getPath(`/st/4/jobs/${sourceJobID}/keep-files`),
                fileIDs.join('\n'),
            )
            .rawResponse()
    }

    public async copyJobToDefaultJob(sourceJobID: JobID): Promise<void> {
        await this.fetchObject
            .post(
                this.hostUrl.getPath(
                    `/st/4/jobs/${sourceJobID}/keep-all-files`,
                ),
            )
            .rawResponse()
    }

    public async uploadFile(
        jobID: string,
        path: string,
        file: File | Blob,
        mtime?: number,
        opt_request?: XMLHttpRequest,
    ): Promise<UploadResponse> {
        const params = {
            path,
            mtime,
            policy: 'no_duplicates',
            want_dimensions: '1',
            want_json: '1',
        }
        const url = this.hostUrl.getPath(`/st/4/jobs/${jobID}/upload`, params)
        const payload = new FormData()
        payload.append('file', file)

        const request = opt_request || new XMLHttpRequest()
        try {
            await new Promise((success, error) => {
                request.addEventListener('load', success)
                request.addEventListener('error', error)
                request.addEventListener('abort', error)

                request.open('POST', url)
                request.send(payload)
            })
        } catch (e) {
            // CAPWEB-2088: Upload can fail because local files have been removed
            if (!(await canReadFileContent(file))) {
                throw new UploadError(
                    UploadFailedReason.LocalFileUnavailable,
                    'File no longer exists',
                )
            }

            throw new UploadError(
                UploadFailedReason.NetworkError,
                'Network error',
            )
        }

        if (
            request.status ===
            413 /* Request Entity Too Large => Out of storage */
        ) {
            throw new UploadError(
                UploadFailedReason.OutOfStorage,
                'Out of storage',
            )
        }

        return JSON.parse(request.responseText)
    }

    // null response value when deleting album file
    public deleteFile(
        jobID: JobID,
        fileID: FileID,
    ): Promise<{ used_space: number } | null> {
        return this.fetchObject
            .post(
                this.hostUrl.getPath(
                    `/st/4/jobs/${jobID}/files_by_id/${fileID}/delete`,
                ),
            )
            .asJson()
    }

    public emptyTrashCan(fileid: FileID): Promise<Response> {
        return this.fetchObject
            .post(this.hostUrl.getPath(`/st/4/empty_trash_can`, { fileid }))
            .rawResponse()
    }

    // null response value when restoring album file
    public restoreFile(
        jobID: JobID,
        fileID: FileID,
    ): Promise<{ used_space: number } | null> {
        return this.fetchObject
            .post(
                this.hostUrl.getPath(`/st/4/jobs/${jobID}/rollback`, {
                    id: fileID,
                }),
            )
            .asJson()
    }

    public addComment(
        jobID: JobID,
        fileID: string,
        comment: string,
    ): Promise<CommentResponse> {
        return this.fetchObject
            .post(
                this.hostUrl.getPath(
                    `/st/4/jobs/${jobID}/files_by_id/${fileID}/comments`,
                ),
                comment,
            )
            .asJson<CommentResponse>()
    }

    public deleteComment(
        jobID: JobID,
        fileID: string,
        commentID: CommentID,
    ): Promise<CommentResponse> {
        return this.fetchObject
            .delete(
                this.hostUrl.getPath(
                    `/st/4/jobs/${jobID}/files_by_id/${fileID}/comments/${commentID}`,
                ),
            )
            .asJson<CommentResponse>()
    }

    public editComment(
        jobID: JobID,
        fileID: string,
        commentID: CommentID,
        commentText: string,
    ): Promise<CommentResponse> {
        return this.fetchObject
            .put(
                this.hostUrl.getPath(
                    `/st/4/jobs/${jobID}/files_by_id/${fileID}/comments/${commentID}`,
                ),
                commentText,
            )
            .asJson<CommentResponse>()
    }

    public subscribeToJob(jobID: JobID): Promise<Response> {
        return this.fetchObject
            .post(this.hostUrl.getPath(`/st/4/jobs/${jobID}/subscribe`))
            .rawResponse()
    }

    public unsubscribeFromJob(jobID: JobID): Promise<Response> {
        return this.fetchObject
            .post(this.hostUrl.getPath(`/st/4/jobs/${jobID}/unsubscribe`))
            .rawResponse()
    }

    public downloadAllFilesInJob(jobID: JobID): Promise<void> {
        // When no files are provided, the backend returns all files in job
        return this.downloadFilesAsArchiveFromJob(
            jobID,
            [],
            PRODUCT_NAME,
            false,
        )
    }

    public getOriginalFileURL(
        jobID: JobID,
        fileID: FileID,
        toJPEG = false,
    ): URLstring {
        return this.hostUrl.getPath(
            `/st/4/jobs/${jobID}/files_by_id/${fileID}`,
            toJPEG ? { to_jpeg: '1' } : undefined,
        )
    }
    public async downloadSingleFileFromJob(
        jobID: JobID,
        fileID: FileID,
        toJPEG = false,
    ): Promise<void> {
        const downloadUrl = this.getOriginalFileURL(jobID, fileID, toJPEG)
        downloadThroughAnchor(downloadUrl)
    }

    /**
     * Endpoint targetted for takeout operations
     * TODO: this will be replaced by appropriate backend endpoint
     */
    public takeoutAllFilesInJob(
        jobID: JobID,
        files: FileID[],
        zipName: string,
    ): Promise<void> {
        return sendPOSTRedirect(
            this.downloadHost.getPath(`/st/4/jobs/${jobID}/files_as_archive`, {
                flattened: '1',
                heic_to_jpeg: '0',
                master_only: '0',
                include_subrevisions: '1',
                zip_filename: zipName,
            }),
            files,
        )
    }

    public downloadFilesAsArchiveFromJob(
        jobID: JobID,
        files: FileID[],
        zipName: string,
        convert: boolean,
        subrevisions: boolean = false,
    ): Promise<any> {
        return sendPOSTRedirect(
            this.downloadHost.getPath(`/st/4/jobs/${jobID}/files_as_archive`, {
                flattened: '1',
                heic_to_jpeg: convert ? '1' : '0',
                master_only: convert ? '1' : '0',
                include_subrevisions: subrevisions ? '1' : '0',
                zip_filename: zipName,
            }),
            files,
        )
    }

    public setJobName(jobID: JobID, name: string): Promise<Response> {
        return this.fetchObject
            .post(this.hostUrl.getPath(`/st/4/jobs/${jobID}/name`, { name }))
            .rawResponse()
    }

    public setCoverPhoto(jobID: JobID, fileID: FileID): Promise<Response> {
        return this.fetchObject
            .post(
                this.hostUrl.getPath(`/st/4/jobs/${jobID}/cover`, {
                    id: fileID,
                }),
            )
            .rawResponse()
    }

    public setPermissionsforJob(
        jobID: JobID,
        permissions: Partial<JobPermissions>,
    ): Promise<PermissionsResponse> {
        return this.fetchObject
            .post(
                this.hostUrl.getPath(
                    `/st/4/jobs/${jobID}/permissions`,
                    permissions,
                ),
            )
            .asJson<PermissionsResponse>()
    }

    public setPrivacyModeForJob(
        jobID: JobID,
        mode: PrivacyMode,
    ): Promise<PrivacyModeResponse> {
        return this.fetchObject
            .post(
                this.hostUrl.getPath(`/st/4/jobs/${jobID}/privacy_mode`, {
                    mode,
                }),
            )
            .asJson<PrivacyModeResponse>()
    }

    public loveFile(jobID: JobID, fileID: FileID): Promise<ReactionResponse> {
        return this.fetchObject
            .post(
                this.hostUrl.getPath(
                    `/st/4/jobs/${jobID}/files_by_id/${fileID}/reaction`,
                ),
                Reactions.Love,
            )
            .asJson<ReactionResponse>()
    }

    public unLoveFile(jobID: JobID, fileID: FileID): Promise<ReactionResponse> {
        return this.fetchObject
            .delete(
                this.hostUrl.getPath(
                    `/st/4/jobs/${jobID}/files_by_id/${fileID}/reaction`,
                ),
            )
            .asJson<ReactionResponse>()
    }

    public getStripeProducts(
        mode: string /* 'test'|'production'*/,
    ): Promise<{ result: StripeProduct[] }> {
        return this.fetchObject
            .get(this.hostUrl.getPath('/st/4/stripe_products', { [mode]: 1 }))
            .asJson()
    }
    public stripePurchase(
        plan: string,
        token: string,
        card: string,
    ): Promise<StripePurchaseResponse> {
        return this.fetchObject
            .post(
                this.hostUrl.getPath('/st/4/stripe_purchase', {
                    plan,
                    token,
                    card,
                }),
            )
            .asJson<StripePurchaseResponse>()
    }
    public validateStripePurchase(
        subscription_id: string,
        payment_intent_id: string,
    ) {
        return this.fetchObject
            .get(
                this.hostUrl.getPath(
                    `/st/4/stripe_purchase/${subscription_id}/validate_purchase`,
                    { payment_intent_id },
                ),
            )
            .rawResponse()
    }

    public postStripePaymentMethod(token: string, card: string) {
        return this.fetchObject
            .post(
                this.hostUrl.getPath('/st/4/stripe_payment_method', {
                    token,
                    card,
                }),
            )
            .rawResponse()
    }

    public getStripePaymentMethodInfo() {
        return this.fetchObject
            .get(this.hostUrl.getPath('/st/4/stripe_payment_method'))
            .asJson<StripePaymentInfoResponse>()
    }

    public getUserGrants() {
        return this.fetchObject
            .get(this.hostUrl.getPath('/st/4/user_grants'))
            .asJson<UserGrantsResponse>()
    }

    public executeGrantLink(link: string) {
        return this.fetchObject.post(link).rawResponse()
    }

    public updateCurrentPlan(plan: string) {
        return this.fetchObject
            .post(
                this.hostUrl.getPath('/st/4/update_stripe_purchase', { plan }),
            )
            .rawResponse()
    }

    public deleteUserCreditCard(
        card_id: string,
    ): Promise<DeleteStripeUserCreditCard> {
        return this.fetchObject
            .delete(
                this.hostUrl.getPath('/st/4/stripe_user_credit_card', {
                    card_id,
                }),
            )
            .asJson()
    }

    public setAlbumAttribute = (
        albumID: JobID,
        attributeName: string,
        attributeValue: string,
    ) => {
        return this.fetchObject
            .put(
                this.hostUrl.getPath(
                    `/st/4/jobs/${albumID}/attribute/${attributeName}`,
                    { value: attributeValue },
                ),
            )
            .rawResponse()
    }

    public getDataProtectionConsentValues(): Promise<{
        data: DataProtectionConsentResponseData
    }> {
        return this.fetchObject
            .get(this.hostUrl.getPath('/st/4/data_protection/consent_values'))
            .asJson()
    }

    public updateDataProtectionConsentValues(
        values: Partial<DataProtectionConsentResponseData>,
    ) {
        return this.fetchObject
            .post(
                this.hostUrl.getPath('/st/4/data_protection/consent_values'),
                JSON.stringify(values),
            )
            .rawResponse()
    }
    public canDownloadDataProtectionRequestedDataAccess(): Promise<{
        data: 'OK'
    }> {
        // Throws 503 if call to request_data_access will yield error due to rate-limiting
        return this.fetchObject
            .get(
                this.hostUrl.getPath(
                    '/st/4/data_protection/request_data_access_availability',
                ),
            )
            .asJson()
    }
    public downloadDataProtectionRequestedDataAccess() {
        window.location.href = this.hostUrl.getPath(
            '/st/4/data_protection/request_data_access',
        )
    }
    public sendDataProtectionRequestForAccountDeletion() {
        return this.fetchObject
            .post(
                this.hostUrl.getPath(
                    '/st/4/data_protection/request_account_deletion',
                ),
            )
            .rawResponse()
    }

    public postUserStatistics(userEvents: UserStatistics[]): Promise<any> {
        return this.fetchObject
            .post(
                this.hostUrl.getPath(`/st/4/user_statistics_events`),
                JSON.stringify({ events: userEvents }),
            )
            .rawResponse()
    }
    public postClientStats(
        device_id: string,
        client_status: ClientStats,
    ): Promise<unknown> {
        return this.fetchObject
            .post(
                this.hostUrl.getPath(`/st/4/client_stats`, { device_id }),
                JSON.stringify(client_status),
            )
            .rawResponse()
    }

    public approveTOS(tos_version: string) {
        return this.fetchObject
            .post(this.hostUrl.getPath(`/st/4/approve_tos`, { tos_version }))
            .rawResponse()
    }

    public getChangesAndAsyncUploadStatus(
        jobID: JobID,
        since: number,
    ): Promise<ChangesAndAsyncUploadStatus> {
        return this.fetchObject
            .get(
                this.hostUrl.getPath(
                    `/st/4/jobs/${jobID}/changes_and_async_upload_status`,
                    { since },
                ),
            )
            .asJson()
    }

    // Test endpoint for deleting stripe data
    public deleteStripeTestData() {
        return this.fetchObject
            .post(this.hostUrl.getPath(`/st/4/test/delete_stripe_data`))
            .rawResponse()
    }

    public async getSupportedExtensions(): Promise<SupportedExtensions> {
        return this.fetchObject
            .get(this.hostUrl.getPath('/st/4/supported_extensions'))
            .asJson()
    }

    // User account atributes endpoints
    public getAccountAttributes(): Promise<{
        result: UserAccountAttribute[]
    }> {
        return this.fetchObject
            .get(this.hostUrl.getPath(`/st/4/account_attribute`))
            .asJson()
    }

    public upsertAccountAttributes(
        body: UserAccountAttributePostParams<UserAccountAttributeKey>,
    ): Promise<{
        result: UserAccountAttribute[]
    }> {
        return this.fetchObject
            .post(
                this.hostUrl.getPath(`/st/4/account_attribute`, {
                    account_attribute_key: body.account_attribute_key,
                }),
                JSON.stringify(body.value),
            )
            .asJson()
    }

    public deleteAccountAttribute(key: UserAccountAttributeKey) {
        return this.fetchObject
            .delete(
                this.hostUrl.getPath(
                    `/st/4/account_attribute?account_attribute_key=${key}`,
                ),
            )
            .rawResponse()
    }
}
